import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Chip, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl,
  Card, CardContent, Grid, CircularProgress, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Tabs, Tab, InputLabel, Link
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as ExtractIcon,
  Refresh as RefreshIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Email as EmailIcon,
  Key as KeyIcon,
  CheckCircle as VerifiedIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  OpenInNew as ExternalLinkIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Provider configurations
const PROVIDER_CONFIG = {
  gmail: {
    name: 'Gmail',
    color: '#EA4335',
    helpUrl: 'https://myaccount.google.com/apppasswords',
    helpText: 'Get Gmail App Password',
    placeholder: 'your.email@gmail.com',
    description: 'Connect your Gmail account using an App Password for secure email extraction.'
  },
  outlook: {
    name: 'Outlook / Office365',
    color: '#0078D4',
    helpUrl: 'https://support.microsoft.com/account-billing/using-app-passwords',
    helpText: 'Get Outlook App Password',
    placeholder: 'your.email@outlook.com',
    description: 'Connect your Microsoft Outlook or Office365 account using an App Password.'
  },
  yahoo: {
    name: 'Yahoo Mail',
    color: '#6001D2',
    helpUrl: 'https://help.yahoo.com/kb/generate-third-party-passwords-sln15241.html',
    helpText: 'Get Yahoo App Password',
    placeholder: 'your.email@yahoo.com',
    description: 'Connect your Yahoo Mail account using an App Password for email extraction.'
  }
};

const EmailProviders = () => {
  const [currentProvider, setCurrentProvider] = useState('gmail');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [extractingId, setExtractingId] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  
  // Form state
  const [newAccount, setNewAccount] = useState({ email: '', appPassword: '', displayName: '' });
  const [addingAccount, setAddingAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Extraction settings
  const [maxResults, setMaxResults] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');

  const config = PROVIDER_CONFIG[currentProvider] || PROVIDER_CONFIG.gmail;

  const loadAccounts = useCallback(async () => {
    try {
      const token = getAuthAccessToken();
      const response = await axios.get(`${API_URL}/email-extractor/providers/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { provider: currentProvider }
      });
      setAccounts(response.data.data?.accounts || []);
    } catch (error) {
      console.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [currentProvider]);

  const loadHistory = useCallback(async () => {
    try {
      const token = getAuthAccessToken();
      const response = await axios.get(`${API_URL}/email-extractor/providers/history`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { provider: currentProvider }
      });
      setHistory(response.data.data || []);
    } catch (error) {
      console.error('Failed to load history');
    }
  }, [currentProvider]);

  useEffect(() => {
    setLoading(true);
    loadAccounts();
    loadHistory();
  }, [loadAccounts, loadHistory]);

  const handleAddAccount = async (e) => {
    e.preventDefault();
    
    if (!newAccount.email || !newAccount.appPassword) {
      toast.error('Please enter email and app password');
      return;
    }

    setAddingAccount(true);
    try {
      const token = getAuthAccessToken();
      await axios.post(`${API_URL}/email-extractor/providers/accounts`, {
        email: newAccount.email,
        appPassword: newAccount.appPassword,
        displayName: newAccount.displayName || newAccount.email,
        provider: currentProvider
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Account added successfully!');
      setNewAccount({ email: '', appPassword: '', displayName: '' });
      setShowAddDialog(false);
      loadAccounts();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to add account';
      toast.error(msg);
    } finally {
      setAddingAccount(false);
    }
  };

  const handleDeleteAccount = async (accountId, email) => {
    if (!window.confirm(`Remove ${email}?`)) return;

    try {
      const token = getAuthAccessToken();
      await axios.delete(`${API_URL}/email-extractor/providers/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(prev => prev.filter(a => a.id !== accountId));
      toast.success('Account removed');
    } catch (error) {
      toast.error('Failed to remove account');
    }
  };

  const handleTestConnection = async (accountId) => {
    try {
      const token = getAuthAccessToken();
      await axios.post(`${API_URL}/email-extractor/providers/accounts/${accountId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Connection successful!');
      loadAccounts();
    } catch (error) {
      toast.error('Connection failed. Update your app password.');
    }
  };

  const handleExtract = async (accountId, { resetCursor = false } = {}) => {
    setExtractingId(accountId);
    setLastResult(null);
    
    try {
      const token = getAuthAccessToken();
      const response = await axios.post(`${API_URL}/email-extractor/providers/accounts/${accountId}/extract`, {
        maxResults,
        searchQuery,
        resetCursor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const stats = response.data?.data?.stats || {};
      setLastResult(stats);
      if (stats.mailboxExhausted) {
        toast.info('No more older messages before the current cursor. Reset the extract cursor to start again from newest.');
      } else {
        toast.success(
          `Batch ${stats.referenceNumber || ''}: ${stats.newEmailsAdded || 0} new, ${stats.duplicateEmails || 0} duplicates (${stats.messagesFetched || stats.messagesProcessed || 0} msgs). Next run continues to older mail.`
        );
      }
      loadHistory();
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Extraction failed');
    } finally {
      setExtractingId(null);
    }
  };

  const handleExtractAll = async () => {
    const verifiedAccounts = accounts.filter(a => a.is_verified);
    if (verifiedAccounts.length === 0) {
      toast.error('No verified accounts available');
      return;
    }

    setLastResult(null);
    let totalNew = 0;
    let totalDuplicates = 0;

    for (const account of verifiedAccounts) {
      try {
        setExtractingId(account.id);
        const token = getAuthAccessToken();
        const response = await axios.post(`${API_URL}/email-extractor/providers/accounts/${account.id}/extract`, {
          maxResults,
          searchQuery
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        totalNew += response.data.data.stats.newEmailsAdded;
        totalDuplicates += response.data.data.stats.duplicateEmails;
      } catch (error) {
        console.error(`Failed to extract from ${account.email}`);
      }
    }

    setExtractingId(null);
    loadHistory();
    loadAccounts();
    
    toast.success(`Complete: ${totalNew} new, ${totalDuplicates} duplicates`);
    setLastResult({ 
      newEmailsAdded: totalNew, 
      duplicateEmails: totalDuplicates, 
      totalAccounts: verifiedAccounts.length 
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
        📧 Email Providers
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Connect email accounts to extract contact information via IMAP
      </Typography>

      {/* Provider Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentProvider} 
          onChange={(e, v) => setCurrentProvider(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {Object.entries(PROVIDER_CONFIG).map(([id, cfg]) => (
            <Tab 
              key={id} 
              value={id} 
              label={cfg.name}
              sx={{ 
                fontWeight: currentProvider === id ? 600 : 400,
                color: currentProvider === id ? cfg.color : 'inherit'
              }}
            />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Provider Description */}
          <Alert severity="info" sx={{ mb: 3 }}>
            {config.description}
            <Link href={config.helpUrl} target="_blank" rel="noopener" sx={{ ml: 1 }}>
              {config.helpText} <ExternalLinkIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
            </Link>
          </Alert>

          {/* Accounts Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Connected Accounts ({accounts.length})
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
            >
              Add Account
            </Button>
          </Box>

          {/* Accounts List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : accounts.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <EmailIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography color="text.secondary">No accounts added yet</Typography>
              <Typography variant="caption" color="text.disabled">
                Click "Add Account" to connect your {config.name}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Last Sync</TableCell>
                    <TableCell>Extract cursor</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map(account => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{account.email}</Typography>
                      </TableCell>
                      <TableCell>{account.display_name}</TableCell>
                      <TableCell align="center">
                        {account.is_verified ? (
                          <Chip icon={<VerifiedIcon />} label="Verified" size="small" color="success" />
                        ) : (
                          <Chip icon={<WarningIcon />} label="Unverified" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(account.last_sync_at)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {account.imap_extract_before_uid != null
                            ? `UID < ${account.imap_extract_before_uid}`
                            : 'Newest first (start)'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {account.admin_enabled === false && (
                          <Chip
                            size="small"
                            color="error"
                            label="Admin locked"
                            sx={{ mr: 1 }}
                            title={account.admin_disabled_reason || 'Disabled by administrator'}
                          />
                        )}
                        <IconButton 
                          size="small" 
                          title="Test Connection"
                          onClick={() => handleTestConnection(account.id)}
                          disabled={account.admin_enabled === false}
                        >
                          <RefreshIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="primary"
                          title="Extract next batch (continues from cursor)"
                          onClick={() => handleExtract(account.id)}
                          disabled={extractingId === account.id || account.admin_enabled === false}
                        >
                          {extractingId === account.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ExtractIcon />
                          )}
                        </IconButton>
                        <IconButton 
                          size="small"
                          title="Reset cursor and extract newest batch"
                          onClick={() => handleExtract(account.id, { resetCursor: true })}
                          disabled={extractingId === account.id || account.admin_enabled === false}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          title="Remove Account"
                          onClick={() => handleDeleteAccount(account.id, account.email)}
                          disabled={account.admin_enabled === false}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Extraction Settings */}
      {accounts.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <ExtractIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Extraction Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Each run processes the next batch of messages (default 100), then moves the IMAP cursor so the following run continues to older mail instead of re-reading the same newest messages. Use the reset-cursor action on an account to start again from the newest mail.
          </Alert>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Messages per Account</InputLabel>
                <Select
                  value={maxResults}
                  label="Messages per Account"
                  onChange={(e) => setMaxResults(e.target.value)}
                  disabled={extractingId}
                >
                  <MenuItem value={50}>50 messages</MenuItem>
                  <MenuItem value={100}>100 messages</MenuItem>
                  <MenuItem value={200}>200 messages</MenuItem>
                  <MenuItem value={500}>500 messages</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Search Filter (Optional)"
                placeholder="e.g., since:2024-01-01"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={extractingId}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                fullWidth
                startIcon={extractingId ? <CircularProgress size={20} color="inherit" /> : <ExtractIcon />}
                onClick={handleExtractAll}
                disabled={extractingId || accounts.filter(a => a.is_verified).length === 0}
              >
                {extractingId ? 'Extracting...' : `Extract from All (${accounts.filter(a => a.is_verified).length})`}
              </Button>
            </Grid>
          </Grid>

          {/* Last Result */}
          {lastResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <strong>Extraction Complete:</strong> {lastResult.newEmailsAdded} new emails added, 
              {lastResult.duplicateEmails} duplicates skipped
              {lastResult.referenceNumber && ` (Ref: ${lastResult.referenceNumber})`}
            </Alert>
          )}
        </Paper>
      )}

      {/* History */}
      {history.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Recent Extractions
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell align="center">New</TableCell>
                  <TableCell align="center">Duplicates</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.slice(0, 10).map(item => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.reference_number}</Typography>
                    </TableCell>
                    <TableCell>{item.filename}</TableCell>
                    <TableCell align="center">
                      <Chip label={item.new_emails_added || 0} size="small" color="success" />
                    </TableCell>
                    <TableCell align="center">{item.duplicate_emails || 0}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={item.status} 
                        size="small" 
                        color={item.status === 'completed' ? 'success' : item.status === 'failed' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add Account Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add {config.name} Account</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleAddAccount} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              placeholder={config.placeholder}
              value={newAccount.email}
              onChange={(e) => setNewAccount(prev => ({ ...prev, email: e.target.value }))}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Display Name (Optional)"
              placeholder="Work Email"
              value={newAccount.displayName}
              onChange={(e) => setNewAccount(prev => ({ ...prev, displayName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="App Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="xxxx xxxx xxxx xxxx"
              value={newAccount.appPassword}
              onChange={(e) => setNewAccount(prev => ({ ...prev, appPassword: e.target.value }))}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <HideIcon /> : <ShowIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 1 }}
            />
            <Link href={config.helpUrl} target="_blank" rel="noopener" sx={{ fontSize: 14 }}>
              <ExternalLinkIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {config.helpText}
            </Link>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddAccount}
            disabled={addingAccount}
            startIcon={addingAccount ? <CircularProgress size={20} /> : <AddIcon />}
          >
            Add & Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailProviders;
